import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  AppBar,
  Toolbar,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
} from '@mui/material';
import { SERVER_URL } from '../../config/apiConfig';
import {
  People as PeopleIcon,
  Menu as MenuIcon,
  ChevronLeft,
  ChevronRight,
  Dashboard as DashboardIcon,
  Subscriptions as SubscriptionsIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { colors } from '../../utils/colors';
import logo from '../../assets/Binan.jpeg';
import Customers from '../../pages/customers/customers';
import EditCustomerContent from '../../pages/customers/EditCustomerContent';
import CustomerRoutes from '../../pages/customers/CustomerRoutes';
import DashboardOverview from '../../pages/dashboard/DashboardOverview';
import Subscriptions from '../../pages/dashboard/Subscriptions';
import Reports from '../../pages/reports/Reports';
import ProfileDrawer from './ProfileDrawer';
import { getAdminProfileAPI } from '../../lib/api';

const withCacheBust = (url) => {
  if (!url) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}cb=${Date.now()}`;
};

const normalizeImageUrl = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return `${SERVER_URL}${url.startsWith('/') ? '' : '/'}${url}`;
};

const Dashboard = ({ defaultSection = 'dashboard', editCustomerId = null }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [profileDrawerOpen, setProfileDrawerOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(defaultSection);
  const [adminImage, setAdminImage] = useState(null);
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [profileLoaded, setProfileLoaded] = useState(false);
  
  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const fetchAdminProfile = useCallback(async (forceRefresh = false) => {
    if (profileLoaded && !forceRefresh) {
      return;
    }
    try {
      const res = await getAdminProfileAPI();
      const data = res?.data || res; 
      const rawImage = data?.profile_image || data?.avatar || null;
      const normalized = normalizeImageUrl(rawImage);
      setAdminImage(normalized ? withCacheBust(normalized) : null);
      setAdminName(data?.name || '');
      setAdminEmail(data?.email || '');
      setProfileLoaded(true);
    } catch (e) {
    }
  }, [profileLoaded]);

  useEffect(() => {
    if (location.pathname.includes('/customers') || location.pathname.includes('/edit-customer')) {
      setActiveSection('customers');
    } else if (location.pathname === '/subscriptions') {
      setActiveSection('subscriptions');
    } else if (location.pathname === '/reports') {
      setActiveSection('reports');
    } else if (location.pathname === '/dashboard' || location.pathname === '/') {
      setActiveSection('dashboard');
    }
  }, [location.pathname]);

  useEffect(() => {
    fetchAdminProfile();
  }, [fetchAdminProfile]);

  const drawerWidth = 210;
  const collapsedDrawerWidth = 80;

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    

    document.cookie.split(";").forEach((cookie) => {
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
    });
    

    navigate('/login');
  };

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    }
  };

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfile = () => {
    handleMenuClose();
    setProfileDrawerOpen(true);
  };

  const handleLogoutClick = () => {
    handleMenuClose();
    handleLogout();
  };

  const handleProfileUpdated = (updated) => {
    if (!updated) return;
    if (updated.preview) {
      setAdminImage(updated.preview);
      setTimeout(() => {
        fetchAdminProfile(true);
      }, 500);
      return;
    }
    if (updated.profile_image || updated.avatar) {
      const normalized = normalizeImageUrl(updated.profile_image || updated.avatar);
      setAdminImage(normalized ? withCacheBust(normalized) : null);
    }
    if (updated.name) {
      setAdminName(updated.name);
    }
    if (updated.email) {
      setAdminEmail(updated.email);
    }
  };

  const handleNavigate = (path) => {
    navigate(path);
    // Close mobile drawer after navigation
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const drawerContent = (isCollapsible = true) => (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          bgcolor: colors.background.primary,
          p: 2.3,
          m: 0,
          height: (isCollapsible && sidebarCollapsed) ? 60 : 80,
          overflow: 'hidden'
        }}
      >
        <Box
          component="img"
          src={logo}
          alt="Logo"
          onClick={() => handleNavigate('/dashboard')}
          sx={{
            width: (isCollapsible && sidebarCollapsed) ? 50 : 120,
            height: '100%',
            objectFit: 'contain',
            cursor: 'pointer'
          }}
        />
      </Box>

      <List sx={{ flexGrow: 1, pt: 2 }}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => handleNavigate('/dashboard')}
            selected={activeSection === 'dashboard'}
            sx={{
              '&.Mui-selected': {
                bgcolor: `${colors.primary.main}20`,
                borderRight: `3px solid ${colors.primary.main}`, 
                '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
                  color: colors.primary.main
                }
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: (isCollapsible && sidebarCollapsed) ? 'auto' : 56, justifyContent: 'center' }}>
              <DashboardIcon sx={{ color: activeSection === 'dashboard' ? colors.primary.main : colors.text.secondary }}/>
            </ListItemIcon>
            {!(isCollapsible && sidebarCollapsed) && (
              <ListItemText
                primary="Dashboard"
                sx={{
                  '& .MuiListItemText-primary': {
                    fontWeight: 550,
                    color: activeSection === 'dashboard' ? colors.primary.main : colors.text.secondary
                  }
                }}
              />
            )}
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => handleNavigate('/customers')}
            selected={activeSection === 'customers'}
            sx={{
              '&.Mui-selected': {
                bgcolor: `${colors.primary.main}20`,
                borderRight: `3px solid ${colors.primary.main}`,
                '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
                  color: colors.primary.main
                }
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: (isCollapsible && sidebarCollapsed) ? 'auto' : 56, justifyContent: 'center' }}>
              <PeopleIcon sx={{ color: activeSection === 'customers' ? colors.primary.main : colors.text.secondary }} />
            </ListItemIcon>
            {!(isCollapsible && sidebarCollapsed) && (
              <ListItemText
                primary="Customers"
                sx={{
                  '& .MuiListItemText-primary': {
                    fontWeight: 550,
                    color: activeSection === 'customers' ? colors.primary.main : colors.text.secondary
                  }
                }}
              />
            )}
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => handleNavigate('/subscriptions')}
            selected={activeSection === 'subscriptions'}
            sx={{
              '&.Mui-selected': {
                bgcolor: `${colors.primary.main}20`,
                borderRight: `3px solid ${colors.primary.main}`,
                '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
                  color: colors.primary.main
                }
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: (isCollapsible && sidebarCollapsed) ? 'auto' : 56, justifyContent: 'center' }}>
              <SubscriptionsIcon sx={{ color: activeSection === 'subscriptions' ? colors.primary.main : colors.text.secondary }} />
            </ListItemIcon>
            {!(isCollapsible && sidebarCollapsed) && (
              <ListItemText
                primary="Subscriptions"
                sx={{
                  '& .MuiListItemText-primary': {
                    fontWeight: 550,
                    color: activeSection === 'subscriptions' ? colors.primary.main : colors.text.secondary
                  }
                }}
              />
            )}
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => handleNavigate('/reports')}
            selected={activeSection === 'reports'}
            sx={{
              '&.Mui-selected': {
                bgcolor: `${colors.primary.main}20`,
                borderRight: `3px solid ${colors.primary.main}`,
                '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
                  color: colors.primary.main
                }
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: (isCollapsible && sidebarCollapsed) ? 'auto' : 56, justifyContent: 'center' }}>
              <AssessmentIcon sx={{ color: activeSection === 'reports' ? colors.primary.main : colors.text.secondary }} />
            </ListItemIcon>
            {!(isCollapsible && sidebarCollapsed) && (
              <ListItemText
                primary="Reports"
                sx={{
                  '& .MuiListItemText-primary': {
                    fontWeight: 550,
                    color: activeSection === 'reports' ? colors.primary.main : colors.text.secondary
                  }
                }}
              />
            )}
          </ListItemButton>
        </ListItem>
      </List>

      {isCollapsible && (
        <Box sx={{ p: 1, borderTop: `1px solid ${colors.border.main}` }}>
          <IconButton
            onClick={handleSidebarToggle}
            disableRipple
            disableFocusRipple
            disableTouchRipple
            sx={{
              width: '100%',
              transition: 'none !important',
              '& .MuiTouchRipple-root': {
                display: 'none'
              }
            }}
          >
            {sidebarCollapsed ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          bgcolor: colors.primary.main,
          width: {
            md: `calc(100% - ${sidebarCollapsed ? collapsedDrawerWidth : drawerWidth}px)`
          },
          ml: {
            md: `${sidebarCollapsed ? collapsedDrawerWidth : drawerWidth}px`
          },
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          transition: 'none !important'
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              mr: { xs: 1.5, sm: 2 },
              display: { md: 'none' },
              width: { xs: 40, sm: 48 },
              height: { xs: 40, sm: 48 }
            }}
          >
            <MenuIcon sx={{ fontSize: { xs: 24, sm: 28 } }} />
          </IconButton>
          <Typography
            variant="h6"
            component="div"
            sx={{
              flexGrow: 1,
              fontWeight: 600,
              fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' }
            }}
          >
            {activeSection === 'dashboard' ? 'Dashboard' :
             activeSection === 'subscriptions' ? 'Subscriptions' :
             activeSection === 'reports' ? 'Reports' :
             location.pathname.includes('/edit-customer') ? 'Edit Customer' : 'Customers'}
          </Typography>

          <IconButton
            onClick={handleMenuClick}
            sx={{
              p: 0,
              width: { xs: 36, sm: 40 },
              height: { xs: 36, sm: 40 }
            }}
          >
            <Avatar
              alt={adminName || 'User'}
              src={adminImage || undefined}
              sx={{
                width: { xs: 36, sm: 40 },
                height: { xs: 36, sm: 40 }
              }}
            />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            onClick={handleMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem onClick={handleProfile}>Profile</MenuItem>
            <MenuItem onClick={handleLogoutClick}>Logout</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{
          width: {
            md: sidebarCollapsed ? collapsedDrawerWidth : drawerWidth
          },
          flexShrink: { md: 0 }
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
                        display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: colors.background.secondary
            },
          }}
        >
          {drawerContent(false)}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: sidebarCollapsed ? collapsedDrawerWidth : drawerWidth,
              bgcolor: colors.background.secondary,
              transition: 'none !important'
            },
          }}
          open
        >
          {drawerContent(true)}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1.5, sm: 2, md: 3 },
          width: {
            md: `calc(100% - ${sidebarCollapsed ? collapsedDrawerWidth : drawerWidth}px)`
          },
          mt: { xs: 7, sm: 7.5, md: 8 },
          minHeight: 'calc(100vh - 64px)',
          bgcolor: colors.background.primary,
          transition: 'none !important'
        }}
      >
        {activeSection === 'dashboard' ? <DashboardOverview /> :
         activeSection === 'subscriptions' ? <Subscriptions /> :
         activeSection === 'reports' ? <Reports /> :
         (location.pathname.includes('/edit-customer') || location.pathname.includes('/customers/')) ? <CustomerRoutes /> : <Customers />}
      </Box>

      <ProfileDrawer
        open={profileDrawerOpen}
        onClose={() => setProfileDrawerOpen(false)}
        onProfileUpdated={handleProfileUpdated}
        initialProfileData={profileLoaded ? { name: adminName, email: adminEmail, profile_image: adminImage } : null}
      />
    </Box>
  );
};

export default Dashboard;